import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
  trackAnalysisClickVBInNewUploads,
  trackAnalysisDeleteClip,
} from '../../../../analytics';

import { PageSubHeader } from '../../../../components/layout/content';

import { URL } from '../../../../constants';

import { fileDelete } from '../../../../services/api/files';
import { queuesDelete } from '../../../../services/api/queues';

import { useVideoBuilderStore } from '../../../../stores/videoBuilder';

import { AnalysisClips, AnalysisFile } from '../../../../types';
import { isEmpty, getPluralPhrase } from '../../../../utils';

import ClipsGroup from './clipsGroup';

interface Props {
  clips: AnalysisClips | null;
  fetchData: () => Promise<void>;
  peopleCount?: number;
  isShowSuccessfulClips?: boolean;
}

const ClipsBlocks: React.FC<Props> = (props) => {
  const { eventId = '' } = useParams();

  const { updateDeletingClipsIds } = useVideoBuilderStore();

  const { clips, fetchData, peopleCount = 0, isShowSuccessfulClips } = props;

  if (!clips || isEmpty(clips)) return null;

  // deletes file and queues associated with it
  const handleDelete = async (video: AnalysisFile, label: string) => {
    try {
      // add elementId to list of deleting clips
      updateDeletingClipsIds(video.elementId, true);

      await queuesDelete(video.elementId);
      await fileDelete(video.elementId);
      await fetchData();

      trackAnalysisDeleteClip(label);
    } catch {
      toast.error(`Delete failed for ${video.fileName}. Try again later`);
    } finally {
      // remove elementId from list of deleting clips
      updateDeletingClipsIds(video.elementId);
    }
  };

  const renderClipsGroup = (
    clipsGroup: AnalysisFile[],
    heading: string,
    noHeading?: boolean,
  ) => {
    if (isEmpty(clipsGroup)) return null;

    return (
      <div className="w-full flex flex-col gap-2">
        {!noHeading && <div className="font-bold">{heading}</div>}

        <ClipsGroup
          clips={clipsGroup}
          handleDelete={(video) => handleDelete(video, heading)}
        />
      </div>
    );
  };

  const hasSuccessfulClips =
    !isEmpty(clips.finishedClips) ||
    !isEmpty(clips.analysingClips) ||
    !isEmpty(clips.convertingClips);

  return (
    <React.Fragment>
      {isShowSuccessfulClips && hasSuccessfulClips && (
        <div className="w-full flex flex-col items-center gap-4">
          <PageSubHeader
            heading="Clips"
            subheading={`We are checking your clips for footage of ${getPluralPhrase(peopleCount, 'person')}.`}
          />

          {renderClipsGroup(clips.finishedClips, 'Done')}

          {renderClipsGroup(clips.analysingClips, 'Analysing')}

          {renderClipsGroup(clips.convertingClips, 'Converting')}
        </div>
      )}

      {!isEmpty(clips.errorClips) && (
        <div className="w-full flex flex-col items-center gap-4">
          <PageSubHeader heading="Analysis failed" />
          {renderClipsGroup(clips.errorClips, 'Analysis failed', true)}
        </div>
      )}

      {!isEmpty(clips.newClips) && (
        <div className="w-full flex flex-col items-center gap-4">
          <PageSubHeader
            heading="New uploads"
            subheading={
              <>
                These clips were added after the analysis had been started. To
                include the new uploads in the final results, go back to{' '}
                <Link
                  to={`/${eventId}${URL.VIDEO_BUILDER}`}
                  onClick={trackAnalysisClickVBInNewUploads}
                >
                  Video Builder
                </Link>{' '}
                and hit 'Analyse' again.
              </>
            }
          />

          {renderClipsGroup(clips.newClips, 'New uploads', true)}
        </div>
      )}
    </React.Fragment>
  );
};

export default ClipsBlocks;
